import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  TextField,
  IconButton,
  CircularProgress,
  Alert,
  Chip,
  Tooltip,
  Paper,
  Stack,
  Divider,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import SaveIcon from "@mui/icons-material/Save";
import RefreshIcon from "@mui/icons-material/Refresh";
import HistoryIcon from "@mui/icons-material/History";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import RestoreIcon from "@mui/icons-material/Restore";
import PlaylistAddIcon from "@mui/icons-material/PlaylistAdd";
import FindReplaceIcon from "@mui/icons-material/FindReplace";

import {
  getExtractionPrompt,
  updateExtractionPrompt,
  regenerateExtractionPrompt,
  aiAssistExtractionPrompt,
  getExtractionPromptVersions,
  rollbackExtractionPromptVersion,
} from "../../services/preonboardingService";

const PromptEditorModal = ({ open, onClose, documentConfig, orgId, customerId, onSaved }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [promptText, setPromptText] = useState("");
  const [currentVersion, setCurrentVersion] = useState(1);
  const [errorMsg, setErrorMsg] = useState("");
  const [successMsg, setSuccessMsg] = useState("");

  // AI Copilot State
  const [aiInstruction, setAiInstruction] = useState("");
  const [aiGenerating, setAiGenerating] = useState(false);
  const [generatedRule, setGeneratedRule] = useState("");

  // Version History State
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [historyVersions, setHistoryVersions] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [selectedVersion, setSelectedVersion] = useState(null);
  const [rollingBack, setRollingBack] = useState(false);

  const mappingId = documentConfig?.id || documentConfig?.mapping_id;

  // Fetch active prompt details
  const fetchPrompt = async () => {
    if (!mappingId) return;
    setLoading(true);
    setErrorMsg("");
    try {
      const res = await getExtractionPrompt(mappingId, orgId, customerId);
      setPromptText(res?.prompt_instructions || res?.prompt_text || res?.prompt || "");
      if (res?.version) {
        setCurrentVersion(res.version);
      }
    } catch (err) {
      console.warn("Could not fetch prompt, default template will be shown:", err);
      setPromptText(
        `Extract the following key fields from the document in strict JSON format:\n- document_number\n- holder_name\n- issue_date\n- expiry_date`
      );
      setCurrentVersion(1);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && mappingId) {
      fetchPrompt();
      setAiInstruction("");
      setGeneratedRule("");
      setSuccessMsg("");
      setErrorMsg("");
    }
  }, [open, mappingId, orgId, customerId]);

  // Save prompt (upsert with version bump)
  const handleSave = async () => {
    if (!promptText.trim()) {
      setErrorMsg("Prompt text cannot be empty.");
      return;
    }
    setSaving(true);
    setErrorMsg("");
    setSuccessMsg("");
    try {
      const res = await updateExtractionPrompt(
        mappingId,
        orgId,
        customerId,
        promptText.trim(),
        `Updated prompt via editor`
      );
      if (res?.version) {
        setCurrentVersion(res.version);
      }
      setSuccessMsg(`Extraction prompt (v${res?.version || currentVersion + 1}) saved successfully!`);
      if (onSaved) onSaved();
      setTimeout(() => {
        onClose();
      }, 1200);
    } catch (err) {
      console.error("Failed to update extraction prompt:", err);
      setErrorMsg(err.response?.data?.detail || err.message || "Failed to update prompt.");
    } finally {
      setSaving(false);
    }
  };

  // Regenerate baseline template from selected fields
  const handleRegenerate = async () => {
    setRegenerating(true);
    setErrorMsg("");
    setSuccessMsg("");
    try {
      const res = await regenerateExtractionPrompt(mappingId, orgId, customerId);
      if (res?.prompt_instructions || res?.prompt_text) {
        setPromptText(res.prompt_instructions || res.prompt_text);
      }
      if (res?.version) {
        setCurrentVersion(res.version);
      }
      setSuccessMsg("Prompt regenerated from field definitions!");
    } catch (err) {
      console.error("Failed to regenerate prompt:", err);
      setErrorMsg(err.response?.data?.detail || err.message || "Failed to regenerate prompt.");
    } finally {
      setRegenerating(false);
    }
  };

  // AI Copilot: Generate rule directive from natural language
  const handleGenerateAiRule = async () => {
    if (!aiInstruction.trim()) {
      setErrorMsg("Please enter an instruction for the AI Copilot.");
      return;
    }
    setAiGenerating(true);
    setErrorMsg("");
    setGeneratedRule("");
    try {
      const res = await aiAssistExtractionPrompt(
        mappingId,
        orgId,
        customerId,
        aiInstruction.trim(),
        promptText
      );
      if (res?.generated_rule) {
        setGeneratedRule(res.generated_rule);
        setSuccessMsg("AI extraction rule generated! Review and insert below.");
      } else {
        setErrorMsg("No rule generated. Please try rephrasing your request.");
      }
    } catch (err) {
      console.error("AI Assist error:", err);
      setErrorMsg(err.response?.data?.detail || err.message || "Failed to generate AI rule.");
    } finally {
      setAiGenerating(false);
    }
  };

  // Append generated rule to existing prompt
  const handleAppendRule = () => {
    if (!generatedRule.trim()) return;
    const current = promptText || "";
    const separator = current.endsWith("\n\n") ? "" : current.endsWith("\n") ? "\n" : "\n\n";
    setPromptText(current + separator + generatedRule.trim());
    setSuccessMsg("AI rule appended to prompt instructions.");
    setGeneratedRule("");
  };

  // Replace prompt with generated rule
  const handleReplaceWithRule = () => {
    if (!generatedRule.trim()) return;
    setPromptText(generatedRule.trim());
    setSuccessMsg("Prompt replaced with AI-generated rule.");
    setGeneratedRule("");
  };

  // Open Version History Dialog
  const handleOpenHistory = async () => {
    setHistoryDialogOpen(true);
    setLoadingHistory(true);
    setErrorMsg("");
    try {
      const res = await getExtractionPromptVersions(mappingId, orgId, customerId);
      const versions = res?.versions || [];
      setHistoryVersions(versions);
      if (versions.length > 0) {
        setSelectedVersion(versions[0]);
      }
    } catch (err) {
      console.error("Failed to fetch version history:", err);
      setErrorMsg("Failed to load version history.");
    } finally {
      setLoadingHistory(false);
    }
  };

  // Rollback to specific version
  const handleRollback = async (versionObj) => {
    if (!versionObj) return;
    const confirmRestore = window.confirm(
      `Restore prompt to version v${versionObj.version}? This will create a new version snapshot and set it active.`
    );
    if (!confirmRestore) return;

    setRollingBack(true);
    try {
      const res = await rollbackExtractionPromptVersion(
        mappingId,
        versionObj.version,
        orgId,
        customerId
      );
      setPromptText(res?.prompt_instructions || versionObj.prompt_instructions);
      if (res?.version) {
        setCurrentVersion(res.version);
      }
      setSuccessMsg(`Restored to version v${versionObj.version} successfully!`);
      setHistoryDialogOpen(false);
      if (onSaved) onSaved();
    } catch (err) {
      console.error("Rollback error:", err);
      setErrorMsg(err.response?.data?.detail || err.message || "Failed to restore version.");
    } finally {
      setRollingBack(false);
    }
  };

  const estimatedTokens = Math.ceil((promptText || "").length / 4);

  return (
    <>
      <Dialog
        open={open}
        onClose={(event, reason) => {
          if (reason === "backdropClick") return;
          if (onClose) onClose(event, reason);
        }}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            p: 1,
            maxHeight: "92vh",
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", pb: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <AutoAwesomeIcon sx={{ color: "#7E22CE", fontSize: 28 }} />
            <Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography variant="h6" fontWeight={700} color="#111827">
                  AI Extraction Prompt
                </Typography>
                <Chip
                  label={`v${currentVersion}`}
                  size="small"
                  sx={{
                    bgcolor: "#F3E8FF",
                    color: "#7E22CE",
                    fontWeight: 700,
                    fontSize: "11px",
                    height: "22px",
                  }}
                />
              </Box>
              <Typography variant="caption" color="text.secondary">
                Configure instructions and validation directives for {documentConfig?.display_name || "Document"}
              </Typography>
            </Box>
          </Box>
          <Stack direction="row" spacing={1} alignItems="center">
            <Button
              variant="outlined"
              size="small"
              startIcon={<HistoryIcon />}
              onClick={handleOpenHistory}
              disabled={loading || saving}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                color: "#7E22CE",
                borderColor: "#D8B4FE",
                "&:hover": { borderColor: "#A855F7", bgcolor: "#FAF5FF" },
                borderRadius: 2,
              }}
            >
              History
            </Button>
            <IconButton onClick={onClose} size="small" disabled={saving || regenerating}>
              <CloseIcon />
            </IconButton>
          </Stack>
        </DialogTitle>

        <DialogContent sx={{ px: 3, py: 1.5 }}>
          {errorMsg && (
            <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setErrorMsg("")}>
              {errorMsg}
            </Alert>
          )}
          {successMsg && (
            <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setSuccessMsg("")}>
              {successMsg}
            </Alert>
          )}

          {/* Document Tags Header */}
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
            <Box sx={{ display: "flex", gap: 1 }}>
              <Chip
                label={documentConfig?.display_name || "Document"}
                size="small"
                sx={{ fontWeight: 600, bgcolor: "#EFF6FF", color: "#1E40AF" }}
              />
              <Chip
                label={documentConfig?.hiring_type === "BLUE_COLLAR" ? "Blue Collar" : "White Collar"}
                size="small"
                variant="outlined"
              />
            </Box>
            <Tooltip title="Regenerate baseline prompt from configured fields">
              <Button
                variant="outlined"
                size="small"
                startIcon={regenerating ? <CircularProgress size={14} /> : <RefreshIcon />}
                onClick={handleRegenerate}
                disabled={loading || saving || regenerating}
                sx={{ textTransform: "none", borderRadius: 2, fontSize: "12px" }}
              >
                Regenerate Baseline
              </Button>
            </Tooltip>
          </Box>

          {/* AI Prompt Copilot (Natural Language Assistant) */}
          <Paper
            elevation={0}
            sx={{
              p: 2,
              mb: 2.5,
              borderRadius: 2.5,
              background: "linear-gradient(135deg, #F0F7FF 0%, #F5F3FF 100%)",
              border: "1px solid #BFDBFE",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
              <AutoAwesomeIcon sx={{ color: "#2563EB", fontSize: 20 }} />
              <Typography variant="subtitle2" fontWeight={700} color="#1E3A8A">
                AI Prompt Copilot (Natural Language Assistant)
              </Typography>
              <Chip
                label="Together AI / Groq"
                size="small"
                sx={{
                  height: 20,
                  fontSize: "10px",
                  fontWeight: 700,
                  bgcolor: "#DBEAFE",
                  color: "#1E40AF",
                }}
              />
            </Box>
            <Typography variant="caption" color="#475569" sx={{ display: "block", mb: 1.5 }}>
              Describe what rule, validation, or extraction formatting you want in plain English (e.g., &ldquo;Aadhaar must be strictly 12 digits, mask the first 8 digits as XXXX-XXXX-1234, format dates as DD/MM/YYYY&rdquo;).
            </Typography>

            <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
              <TextField
                fullWidth
                size="small"
                placeholder='e.g. "Ensure Aadhaar number is 12 digits, mask first 8 digits, format date as DD/MM/YYYY"'
                value={aiInstruction}
                onChange={(e) => setAiInstruction(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !aiGenerating) {
                    e.preventDefault();
                    handleGenerateAiRule();
                  }
                }}
                disabled={aiGenerating}
                sx={{
                  bgcolor: "#FFFFFF",
                  "& .MuiOutlinedInput-root": {
                    borderColor: "#93C5FD",
                    borderRadius: 2,
                  },
                }}
              />
              <Button
                variant="contained"
                onClick={handleGenerateAiRule}
                disabled={aiGenerating || !aiInstruction.trim()}
                startIcon={aiGenerating ? <CircularProgress size={16} color="inherit" /> : <AutoAwesomeIcon />}
                sx={{
                  whiteSpace: "nowrap",
                  textTransform: "none",
                  fontWeight: 600,
                  bgcolor: "#2563EB",
                  "&:hover": { bgcolor: "#1D4ED8" },
                  px: 2.5,
                  borderRadius: 2,
                }}
              >
                {aiGenerating ? "Generating..." : "Generate Rule"}
              </Button>
            </Box>

            {/* Generated Rule Preview & Insertion Options */}
            {generatedRule && (
              <Box sx={{ mt: 2, pt: 1.5, borderTop: "1px dashed #BFDBFE" }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.75 }}>
                  <Typography variant="caption" fontWeight={700} color="#1E40AF">
                    Generated Rule Directive:
                  </Typography>
                  <Stack direction="row" spacing={1}>
                    <Button
                      size="small"
                      variant="text"
                      onClick={() => {
                        navigator.clipboard.writeText(generatedRule);
                        setSuccessMsg("Generated rule copied to clipboard!");
                      }}
                      startIcon={<ContentCopyIcon sx={{ fontSize: 13 }} />}
                      sx={{ textTransform: "none", fontSize: "11px", color: "#475569" }}
                    >
                      Copy
                    </Button>
                    <Button
                      size="small"
                      variant="text"
                      onClick={() => setGeneratedRule("")}
                      sx={{ textTransform: "none", fontSize: "11px", color: "#94A3B8" }}
                    >
                      Clear
                    </Button>
                  </Stack>
                </Box>

                <Paper
                  elevation={0}
                  sx={{
                    p: 1.5,
                    bgcolor: "#FFFFFF",
                    border: "1px solid #CBD5E1",
                    borderRadius: 1.5,
                    fontFamily: 'Consolas, Monaco, "Courier New", monospace',
                    fontSize: "12px",
                    lineHeight: 1.5,
                    color: "#0F172A",
                    maxHeight: "130px",
                    overflowY: "auto",
                    whiteSpace: "pre-wrap",
                    mb: 1.5,
                  }}
                >
                  {generatedRule}
                </Paper>

                <Stack direction="row" spacing={1.5}>
                  <Button
                    size="small"
                    variant="contained"
                    onClick={handleAppendRule}
                    startIcon={<PlaylistAddIcon sx={{ fontSize: 16 }} />}
                    sx={{
                      bgcolor: "#1E40AF",
                      "&:hover": { bgcolor: "#1E3A8A" },
                      textTransform: "none",
                      fontWeight: 600,
                      fontSize: "12px",
                      borderRadius: 1.5,
                    }}
                  >
                    Append to Prompt
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={handleReplaceWithRule}
                    startIcon={<FindReplaceIcon sx={{ fontSize: 16 }} />}
                    sx={{
                      textTransform: "none",
                      fontWeight: 600,
                      fontSize: "12px",
                      color: "#1E40AF",
                      borderColor: "#93C5FD",
                      borderRadius: 1.5,
                    }}
                  >
                    Replace Entire Prompt
                  </Button>
                </Stack>
              </Box>
            )}
          </Paper>

          {/* Main Prompt Instructions Editor */}
          <Box sx={{ mb: 1 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.5 }}>
              <Typography variant="caption" fontWeight={600} color="#475569">
                SYSTEM PROMPT INSTRUCTIONS
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {promptText.length} characters • ~{estimatedTokens} tokens
              </Typography>
            </Box>

            {loading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
                <CircularProgress />
              </Box>
            ) : (
              <TextField
                multiline
                rows={11}
                fullWidth
                value={promptText}
                onChange={(e) => setPromptText(e.target.value)}
                placeholder="System prompt instructions for AI extraction..."
                variant="outlined"
                InputProps={{
                  sx: {
                    fontFamily: 'Consolas, Monaco, "Courier New", monospace',
                    fontSize: "0.85rem",
                    bgcolor: "#F8FAFC",
                    borderRadius: 2,
                    lineHeight: 1.6,
                  },
                }}
              />
            )}
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2, pt: 1, gap: 1 }}>
          <Button onClick={onClose} disabled={saving} sx={{ textTransform: "none", color: "#6B7280" }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
            onClick={handleSave}
            disabled={loading || saving || regenerating}
            sx={{
              bgcolor: "#7E22CE",
              "&:hover": { bgcolor: "#6B21A8" },
              textTransform: "none",
              borderRadius: 2,
              px: 3,
              fontWeight: 600,
            }}
          >
            {saving ? "Saving..." : "Save Prompt"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Version History Dialog */}
      <Dialog
        open={historyDialogOpen}
        onClose={(event, reason) => {
          if (reason === "backdropClick") return;
          setHistoryDialogOpen(false);
        }}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, p: 1, maxHeight: "90vh" } }}
      >
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", pb: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <HistoryIcon sx={{ color: "#7E22CE", fontSize: 24 }} />
            <Box>
              <Typography variant="h6" fontWeight={700} color="#0F172A">
                Version History: {documentConfig?.display_name || "Document"}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Active Version: v{currentVersion} • Total Revisions: {historyVersions.length}
              </Typography>
            </Box>
          </Box>
          <IconButton size="small" onClick={() => setHistoryDialogOpen(false)}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <Divider />

        <DialogContent sx={{ p: 2.5 }}>
          {loadingHistory ? (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "260px" }}>
              <CircularProgress size={32} sx={{ color: "#7E22CE" }} />
            </Box>
          ) : (
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "260px 1fr" }, gap: 2.5, minHeight: "360px" }}>
              {/* Left Pane: Version Timeline */}
              <Box sx={{ borderRight: { md: "1px solid #E2E8F0" }, pr: { md: 2 } }}>
                <Typography
                  variant="caption"
                  fontWeight={700}
                  color="#64748B"
                  textTransform="uppercase"
                  letterSpacing={0.5}
                  sx={{ mb: 1.5, display: "block" }}
                >
                  Revision Timeline
                </Typography>
                <Stack spacing={1.5} sx={{ maxHeight: "360px", overflowY: "auto", pr: 0.5 }}>
                  {historyVersions.length === 0 ? (
                    <Typography variant="caption" color="text.secondary">
                      No prior versions recorded yet.
                    </Typography>
                  ) : (
                    historyVersions.map((v) => {
                      const isSelected = selectedVersion?.version === v.version;
                      return (
                        <Paper
                          key={v.version}
                          elevation={0}
                          onClick={() => setSelectedVersion(v)}
                          sx={{
                            p: 1.5,
                            cursor: "pointer",
                            borderRadius: 2,
                            border: isSelected ? "2px solid #7E22CE" : "1px solid #E2E8F0",
                            bgcolor: isSelected ? "#FAF5FF" : "#FFFFFF",
                            transition: "all 0.15s ease",
                            "&:hover": {
                              borderColor: isSelected ? "#7E22CE" : "#C4B5FD",
                              bgcolor: isSelected ? "#FAF5FF" : "#FDF4FF",
                            },
                          }}
                        >
                          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.5 }}>
                            <Stack direction="row" spacing={0.75} alignItems="center">
                              <Chip
                                label={`v${v.version}`}
                                size="small"
                                sx={{
                                  fontWeight: 700,
                                  fontSize: "11px",
                                  height: "20px",
                                  bgcolor: v.is_current ? "#DCFCE7" : "#F1F5F9",
                                  color: v.is_current ? "#15803D" : "#475569",
                                }}
                              />
                              {v.is_current && (
                                <Chip
                                  label="ACTIVE"
                                  size="small"
                                  sx={{
                                    fontWeight: 700,
                                    fontSize: "9px",
                                    height: "18px",
                                    bgcolor: "#16A34A",
                                    color: "#FFFFFF",
                                  }}
                                />
                              )}
                            </Stack>
                            <Typography variant="caption" color="#94A3B8" fontSize="11px">
                              {v.created_at ? new Date(v.created_at).toLocaleDateString() : ""}
                            </Typography>
                          </Box>
                          <Typography variant="caption" color="#475569" fontWeight={500} display="block" sx={{ mb: 0.5 }}>
                            {v.change_summary || "Prompt instructions update"}
                          </Typography>
                          <Typography variant="caption" color="#94A3B8" fontSize="11px">
                            By: {v.created_by || "Admin"} • Source: {v.source}
                          </Typography>
                        </Paper>
                      );
                    })
                  )}
                </Stack>
              </Box>

              {/* Right Pane: Selected Version Preview */}
              <Box sx={{ display: "flex", flexDirection: "column" }}>
                {selectedVersion ? (
                  <>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.5, flexWrap: "wrap", gap: 1 }}>
                      <Box>
                        <Typography variant="subtitle2" fontWeight={700} color="#0F172A">
                          Viewing Version v{selectedVersion.version}
                        </Typography>
                        <Typography variant="caption" color="#64748B">
                          Saved on {selectedVersion.created_at ? new Date(selectedVersion.created_at).toLocaleString() : "N/A"} • {selectedVersion.created_by || "Admin"}
                        </Typography>
                      </Box>
                      <Stack direction="row" spacing={1}>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => {
                            navigator.clipboard.writeText(selectedVersion.prompt_instructions);
                            setSuccessMsg("Version prompt text copied to clipboard!");
                          }}
                          startIcon={<ContentCopyIcon sx={{ fontSize: 13 }} />}
                          sx={{ textTransform: "none", fontSize: "12px", borderRadius: 1.5 }}
                        >
                          Copy
                        </Button>
                        {!selectedVersion.is_current && (
                          <Button
                            size="small"
                            variant="contained"
                            disabled={rollingBack}
                            onClick={() => handleRollback(selectedVersion)}
                            startIcon={rollingBack ? <CircularProgress size={14} color="inherit" /> : <RestoreIcon sx={{ fontSize: 14 }} />}
                            sx={{
                              textTransform: "none",
                              fontSize: "12px",
                              fontWeight: 600,
                              bgcolor: "#7E22CE",
                              "&:hover": { bgcolor: "#6B21A8" },
                              borderRadius: 1.5,
                            }}
                          >
                            {rollingBack ? "Restoring..." : `Restore to v${selectedVersion.version}`}
                          </Button>
                        )}
                      </Stack>
                    </Box>

                    <Paper
                      elevation={0}
                      sx={{
                        flex: 1,
                        p: 2,
                        bgcolor: "#F8FAFC",
                        border: "1px solid #E2E8F0",
                        borderRadius: 2,
                        fontFamily: 'Consolas, Monaco, "Courier New", monospace',
                        fontSize: "12px",
                        lineHeight: 1.6,
                        maxHeight: "300px",
                        overflowY: "auto",
                        whiteSpace: "pre-wrap",
                      }}
                    >
                      {selectedVersion.prompt_instructions}
                    </Paper>
                  </>
                ) : (
                  <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
                    <Typography variant="body2" color="text.secondary">
                      Select a version on the left to preview its instructions.
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 2.5, pb: 2 }}>
          <Button onClick={() => setHistoryDialogOpen(false)} sx={{ textTransform: "none" }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default PromptEditorModal;
